/**
 * Splits an already-sanitized blog post HTML string into a typed array of
 * content blocks (heading / paragraph / list / table / image / raw).
 *
 * This is a purely structural pass, not a redesign: every block still
 * carries its exact original outerHTML — same tags, same attributes
 * (including whatever style/class the legacy WordPress import + the
 * existing styledContent() injection already produced), same content,
 * same order. Splitting the single HTML blob into typed blocks changes
 * nothing about what gets rendered; it only makes the structure
 * inspectable (which chunk is a heading, a list, a table, an image) for
 * whatever the frontend wants to build on top later (a table of contents,
 * per-block styling hooks, reading-time estimates, …).
 *
 * A future CMS-authored post can produce `ContentBlock[]` directly,
 * bypassing this parser entirely, and still render through the exact same
 * `<BlogContentBlocks>` component used today for parsed WordPress imports.
 */
export interface HeadingBlock {
  type: 'heading';
  level: 2 | 3;
  html: string;
}

export interface ParagraphBlock {
  type: 'paragraph';
  html: string;
}

export interface ListBlock {
  type: 'list';
  ordered: boolean;
  itemCount: number;
  html: string;
}

export interface TableBlock {
  type: 'table';
  rowCount: number;
  html: string;
}

export interface ImageBlock {
  type: 'image';
  html: string;
}

/** Fallback for anything not explicitly modeled above — keeps content
 * parity guaranteed even for markup shapes this parser doesn't recognize. */
export interface RawBlock {
  type: 'raw';
  html: string;
}

export type ContentBlock =
  | HeadingBlock
  | ParagraphBlock
  | ListBlock
  | TableBlock
  | ImageBlock
  | RawBlock;

/** WordPress wraps a single image in `<figure>`, sometimes inside a
 * `<div class="wp-block-image ...">`. Recurse through single-child div
 * wrappers to find the figure/img underneath. */
function isImageWrapper(el: Element): boolean {
  const tag = el.tagName.toLowerCase();
  if (tag === 'img' || tag === 'figure') return true;
  if (tag === 'div') {
    const children = el.children;
    return children.length === 1 && isImageWrapper(children[0]);
  }
  return false;
}

export function parseContentBlocks(sanitizedHtml: string): ContentBlock[] {
  const doc = new DOMParser().parseFromString(sanitizedHtml, 'text/html');
  const blocks: ContentBlock[] = [];

  // Some imported posts don't wrap body text in <p> at all — they use bare
  // top-level <span>/text nodes instead (inline by default). Each recognized
  // block below (h2/p/ul/table/...) is genuinely block-level in HTML, so
  // giving it its own wrapper <div> is a no-op for layout. Inline content is
  // not — wrapping every stray <span> in its own block-level <div> would
  // force a line break between runs that flow together today. So unclassified
  // content is buffered and flushed as one combined block, keeping it in the
  // same shared container it already had.
  let rawBuffer = '';
  const flushRaw = () => {
    if (rawBuffer.trim().length > 0) blocks.push({ type: 'raw', html: rawBuffer });
    rawBuffer = '';
  };

  for (const node of Array.from(doc.body.childNodes)) {
    if (node.nodeType === Node.TEXT_NODE) {
      const text = node.textContent ?? '';
      if (text.trim().length === 0) continue;
      const span = doc.createElement('span');
      span.textContent = text;
      rawBuffer += span.outerHTML;
      continue;
    }
    if (node.nodeType !== Node.ELEMENT_NODE) continue;

    const el = node as Element;
    const tag = el.tagName.toLowerCase();

    if (tag === 'h2' || tag === 'h3') {
      flushRaw();
      blocks.push({ type: 'heading', level: tag === 'h2' ? 2 : 3, html: el.outerHTML });
    } else if (tag === 'p') {
      flushRaw();
      blocks.push({ type: 'paragraph', html: el.outerHTML });
    } else if (tag === 'ul' || tag === 'ol') {
      flushRaw();
      blocks.push({ type: 'list', ordered: tag === 'ol', itemCount: el.querySelectorAll(':scope > li').length, html: el.outerHTML });
    } else if (tag === 'table') {
      flushRaw();
      blocks.push({ type: 'table', rowCount: el.querySelectorAll('tr').length, html: el.outerHTML });
    } else if (isImageWrapper(el)) {
      flushRaw();
      blocks.push({ type: 'image', html: el.outerHTML });
    } else {
      rawBuffer += el.outerHTML;
    }
  }
  flushRaw();

  return blocks;
}
